<template>
  <k-modal :show="show" @close="closeModal" modal-size="modal-lg">
    <template #header>
      <h3>Submission History</h3>
    </template>
    <template #body>
      <k-table v-if="otjHistoryReady"
        :headers="kableHeaders"
        :rows="kableRows"
        :hideable="false"
        :panel="false"
        :max="5"
        @clicked="handleTableAction"
      ></k-table>
    </template>
    <template #footer>
      <button class="btn btn-primary" @click="closeModal">Close</button>
    </template>
  </k-modal>
</template>

<script>
import KModal from '@base-components/k-modal.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import TimeMixin from '@mixins/time-mixins';
import { sortObjectArray } from '@utils/sort-by-object-property';

export default {
  name: 'OtjSubmissionHistory',
  components: {
    KModal,
    KTable,
  },

  mixins: [TimeMixin],

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    otjEntryId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      otjHistoryReady: false,
      otjHistory: [],
    };
  },

  mounted() {
    this.getSubmissionHistory();
  },

  watch: {
    show(newValue) {
      if (newValue && this.otjEntryId) {
        this.getSubmissionHistory();
      } else {
        this.otjHistory = []; // Clear data when modal is hidden
      }
    },
  },

  computed: {
    kableHeaders() {
      return {
        date: { name: 'Date', type: 'string' },
        time_spent: { name: 'Duration', type: 'string' },
        status: {
          name: 'Status',
          type: 'pill',
          options: {
            type: status => {
              const statusMap = {
                approved: 'success',
                rejected: 'danger',
                pending: 'primary',
                draft: 'neutral',
              };
              return statusMap[status.toLowerCase()] || 'neutral';
            },
          },
        },
        learning_activity: { name: 'Activity', type: 'string' },
        learning_description: { name: 'Description', type: 'string' },
        coach_notes: { name: 'Coach Notes', type: 'string' },
      };
    },

    kableRows() {
      return sortObjectArray(this.otjHistory, 'id').map(otj => ({
        id: otj.id,
        date: this.parseTimestamp(otj.start_time, undefined, 'dd/MM/yyyy'),
        time_spent: this.formatTimeSpent(otj.time_spent),
        status: otj.status,
        learning_activity: otj.category,
        learning_description: otj.learning_description,
        coach_notes: otj.rejection_notes || 'No feedback provided.',
      }));
    },
  },

  methods: {
    closeModal() {
      this.$emit('close');
    },

    getSubmissionHistory() {
      if (!this.otjEntryId) {
        return;
      }
      this.$Loading.start();
      this.otjHistoryReady = false;
      this.$http.get(`/api/curriculum/programmes/otj-entries/${this.otjEntryId}/submission-history`)
        .then(response => {
          this.otjHistory = response.data || [];
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching OTJ submission history', { otjEntryId: this.otjEntryId }, err);
          this.$ktoast.warning('Error fetching submission history.');
          this.otjHistory = [];
        })
        .finally(() => {
          this.otjHistoryReady = true;
          this.$Loading.finish();
        });
    },

    formatTimeSpent(minutes) {
      const minutesValue = minutes ?? 0;
      const hours = Math.floor(minutesValue / 60);
      const remainingMinutes = minutesValue % 60;
      if (hours === 0) {
        return `${remainingMinutes}m`;
      }
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    },
  },
};
</script>
